import React from 'react';
import { useIntl } from 'react-intl';

import styles from './HashResultTemplate.module.less';
import OperondaLogo from '@src/components/OperondaLogo';
import ReportTable from './ReportTable';

export interface HashResultGroupField {
  fieldName: string;
  fieldValue: string;
}

export interface HashResultGroup {
  groupName: string;
  fields: HashResultGroupField[];
}

export type DailyReportColumnKey =
  | 'date'
  | 'employerName'
  | 'employerIdentifier'
  | 'address'
  | 'agreedWorkingHours'
  | 'markingJourneyHours'
  | 'breakTime'
  | 'actualBreakTime';

export interface IDailyReportColumn {
  dataIndex: DailyReportColumnKey;
  title: string;
}

export interface IDailyReportCell {
  value: string;
}

export interface IDailyReportRow {
  cells: IDailyReportCell[];
}

export interface IDailyReport {
  columns: IDailyReportColumn[];
  rows: IDailyReportRow[];
}

export interface HashResultTemplateProps {
  title?: string;
  headerText?: string;
  dateSentEmail?: string;
  groups?: HashResultGroup[];
  description?: string;
  additionalInfo?: string;
  dailyReport?: IDailyReport;
}

const HashResultTemplate = ({
  title = '',
  headerText = '',
  dateSentEmail = '',
  groups = [],
  description = '',
  additionalInfo = '',
  dailyReport,
}: HashResultTemplateProps) => {
  const intl = useIntl();

  const isEmail = (value: string): boolean => typeof value === 'string' && value.includes('@');

  const renderFieldValue = (value: string): React.ReactNode => {
    if (isEmail(value)) {
      return (
        <a href={`mailto:${value}`} className={styles.emailLink}>
          {value}
        </a>
      );
    }
    return value;
  };

  const dailyReportColumns =
    dailyReport?.columns?.map(column => ({
      title: column.title,
      dataIndex: column.dataIndex,
    })) || [];

  const dailyReportData =
    dailyReport?.rows?.map((row, rowIndex) => {
      const mappedRow: Record<string, string | number> = { id: `daily-report-row-${rowIndex}` };

      dailyReportColumns.forEach((column, cellIndex) => {
        mappedRow[column.dataIndex] = row.cells[cellIndex]?.value ?? '';
      });

      return mappedRow;
    }) || [];

  return (
    <div className={styles.container}>
      <div className={styles.detailSection}>
        <div className={styles.logoContainer}>
          <OperondaLogo />
        </div>

        <div className={styles.header}>
          <h1 className={styles.headerText}>{headerText || title}</h1>
          {dateSentEmail && <span className={styles.date}>{dateSentEmail}</span>}
        </div>

        {groups && groups.length > 0 && (
          <div className={styles.sectionsContainer}>
            {groups.map((group, groupIndex) => {
              if (!group.fields || group.fields.length === 0) return null;

              return (
                <div key={groupIndex} className={styles.section}>
                  <h3 className={styles.sectionTitle}>{group.groupName}</h3>
                  <div className={styles.fieldsContainer}>
                    {group.fields.map((field, fieldIndex) => (
                      <div key={fieldIndex} className={styles.fieldRow}>
                        <div className={styles.fieldLabel}>{field.fieldName}</div>
                        <div className={styles.fieldValue}>
                          {renderFieldValue(field.fieldValue)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {dailyReportColumns.length > 0 && dailyReportData.length > 0 && (
        <div className={`${styles.detailSection} ${styles.dailyReportWrap}`}>
          <ReportTable
            rowKey="id"
            data={dailyReportData}
            headerOne={[]}
            headerTwo={[]}
            columns={dailyReportColumns}
            footer={null}
            summaryData={null}
            disableScroll={true}
          />
        </div>
      )}

      {(description || additionalInfo) && (
        <div className={styles.detailSection}>
          <h3 className={styles.sectionTitle}>
            {intl.formatMessage({ id: 'hashResult.subject' })}
          </h3>
          {description && <p className={styles.description}>{description}</p>}
          {additionalInfo && <p className={styles.description}>{additionalInfo}</p>}
        </div>
      )}
    </div>
  );
};

export default HashResultTemplate;
